import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { captureReferralFromURL, applyPendingReferralIfAuthenticated } from './lib/referralLink'
import { Landing }   from './routes/Landing'
import { Login }     from './routes/Login'
import { Dashboard } from './routes/Dashboard'
import { BugReport } from './routes/BugReport'
import { Profile }   from './routes/Profile'
import { Shop }      from './routes/Shop'
import { Org }       from './routes/Org'
import { Chat }      from './routes/Chat'
import { Punch }     from './routes/Punch'
import { Recruitment } from './routes/Recruitment'
import { Apply }     from './routes/Apply'
import { JoinInvite } from './routes/JoinInvite'
import { Payroll }   from './routes/Payroll'
import { Shifts }    from './routes/Shifts'
import { SickLeaves } from './routes/SickLeaves'
import { PatchNotes } from './routes/PatchNotes'
import { Server }     from './routes/Server'
import { AppShell }  from './components/AppShell'
import { useAuth }   from './lib/useAuth'

export function App() {
  const { user, loading } = useAuth()

  // Beim allerersten Mount: ?ref=CODE aus der URL einlesen + persistieren.
  // Sobald der User auth'd ist, ruft useAuth → broadcast() den Apply
  // automatisch auf — siehe useAuth.ts.
  useEffect(() => { captureReferralFromURL() }, [])

  // Edge-Case: User war beim Capture schon eingeloggt → broadcast triggert
  // nicht (kein Auth-Wechsel). Daher hier zusätzlich versuchen sobald
  // auth-State geladen ist UND ein Code im localStorage liegt.
  useEffect(() => {
    if (!loading && user) {
      void applyPendingReferralIfAuthenticated()
    }
  }, [loading, user?.id])

  if (loading) return <SplashScreen />

  // Auth-Guard für eingeloggte Routen
  const guarded = (el: React.ReactNode) =>
    user ? <AppShell>{el}</AppShell> : <Navigate to="/login" replace />

  return (
    <Routes>
      <Route path="/"         element={<Landing />} />
      <Route path="/patchnotes" element={<PatchNotes />} />
      <Route path="/server"     element={<Server />} />
      <Route path="/login"    element={user ? <Navigate to="/app" replace /> : <Login />} />

      {/* Öffentliche Bewerbungs-Seite — KEIN Auth-Guard.
          Bekommt der Bewerber von einer Firma als Code-URL. */}
      <Route path="/apply"        element={<Apply />} />
      <Route path="/apply/:code"  element={<Apply />} />

      {/* Org-Einladungslink — User landet hier wenn er einen Code-Link
          aus iOS/Webapp anklickt. Auth-frei (Preview), join danach mit Auth. */}
      <Route path="/join/:code"   element={<JoinInvite />} />

      <Route path="/app"         element={guarded(<Dashboard />)} />
      <Route path="/app/bug"     element={guarded(<BugReport />)} />
      <Route path="/app/profile" element={guarded(<Profile />)} />
      <Route path="/app/shop"    element={guarded(<Shop />)} />
      <Route path="/app/org"     element={guarded(<Org />)} />
      <Route path="/app/chat"    element={guarded(<Chat />)} />
      <Route path="/app/punch"       element={guarded(<Punch />)} />
      <Route path="/app/recruitment" element={guarded(<Recruitment />)} />
      <Route path="/app/payroll"     element={guarded(<Payroll />)} />
      <Route path="/app/shifts"      element={guarded(<Shifts />)} />
      <Route path="/app/sick-leaves" element={guarded(<SickLeaves />)} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function SplashScreen() {
  return (
    <div className="h-full flex items-center justify-center bg-app-bg">
      <div className="w-12 h-12 rounded-full border-4 border-app-border border-t-accent animate-spin" />
    </div>
  )
}
